import { Fade, Grid, TableRow, Typography } from "@mui/material"
import { FunctionComponent, SVGProps } from "react"
import { NavigateFunction } from "react-router-dom"
import { Button, tokenMapping } from "../commons"
import { CustomizedTablesProps, StyledTableCell } from "../commons/CompactTable"
import { displayNumber } from "../commons/utils"
import { UNKNOWNIcon } from "../../assets"

export const tableHeadersDeposit: CustomizedTablesProps['tableHeader'] = [
  { label: 'Assets', props: { sx: { paddingLeft: '1.5rem' } } },
  { label: 'Deposited' },
  { label: 'APY' },
  { label: '' },
]

export const tableHeadersBorrow: CustomizedTablesProps['tableHeader'] = [
  { label: 'Assets' },
  { label: 'Debt' },
  { label: 'APY' },
  { label: '' },
]

export interface BorrowAndLendingDataType {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>,
  asset: string
  name: string
  price: number
  supplyApy: number
  supply: number
  supplyValue: number
  borrowApy: number
  borrow: number
  borrowValue: number
}

export const constructBorrowAndLendingTable = (datas: BorrowAndLendingDataType[], isDeposited: boolean, isBorrowed: boolean, navigate: NavigateFunction) => {

  const navigateTo = (path: string) => {
    navigate(path)
  }

  return datas.map((data, index) => {
    const { asset, supply, supplyValue, supplyApy, borrow, borrowApy, borrowValue } = data
    const assetAmount = isDeposited ? supply : borrow
    const assetApy = isDeposited ? supplyApy : borrowApy
    const assetValue = isDeposited ? supplyValue : borrowValue
    const TokenIcon = tokenMapping[asset.toUpperCase()]?.iconUrl ?? UNKNOWNIcon
    const animationTimeout = (2000 * index + 1) > 3000 ? 3000 : 2000 * index + 1
    return (
      <Fade in timeout={animationTimeout}>
        <TableRow>
          <StyledTableCell sx={{ paddingLeft: '1.5rem' }}>
            <Grid container alignItems={'center'} gap={1}>
              <TokenIcon />
              <Typography variant='s'>
                {asset}
              </Typography>
            </Grid>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography variant='s' component={'div'}>{`${displayNumber(assetAmount, 6, 2)}`}</Typography>
            <Typography variant='xs' color='text.secondary'>${displayNumber(assetValue, 2, 2)}</Typography>
          </StyledTableCell>
          <StyledTableCell align="left">{assetApy.toFixed(2)}%</StyledTableCell>
          <StyledTableCell align="right">
            {
              isDeposited && (
                <Grid container gap={1} justifyContent={'flex-end'}>
                  <Button variant='contained' color="secondary" onClick={() => navigateTo(`/token/${data.asset}?type=deposit`)}>
                    Deposit
                  </Button>
                  <Button variant='contained' color="secondary" onClick={() => navigateTo(`/token/${data.asset}?type=withdraw`)}>
                    Withdraw
                  </Button>
                </Grid>
              )
            }
            {
              isBorrowed && (
                <Grid container gap={1} justifyContent={'flex-end'}>
                  <Button variant='contained' color="secondary" onClick={() => navigateTo(`/token/${data.asset}?type=borrow`)}>
                    Borrow
                  </Button>
                  <Button variant='contained' color="secondary" onClick={() => navigateTo(`/token/${data.asset}?type=repay`)}>
                    Repay
                  </Button>
                </Grid>
              )
            }
          </StyledTableCell>
        </TableRow>
      </Fade>
    )
  })
}

export const constructBorrowAndLendingTableMobile = (datas: BorrowAndLendingDataType[], isDeposited: boolean, isBorrowed: boolean, navigate: NavigateFunction) => {

  const navigateTo = (path: string) => {
    navigate(path)
  }

  return datas.map((data) => {
    const { asset, supply, supplyValue, supplyApy, borrow, borrowApy, borrowValue } = data
    const assetAmount = isDeposited ? supply : borrow
    const assetApy = isDeposited ? supplyApy : borrowApy
    const assetValue = isDeposited ? supplyValue : borrowValue
    const TokenIcon = tokenMapping[asset.toUpperCase()]?.iconUrl ?? UNKNOWNIcon
    return (
      <TableRow>
        <StyledTableCell sx={{ paddingLeft: '1rem', borderTop: '1px solid' }}>
          <Grid container alignItems={'center'} gap={2}>
            <Grid item container alignItems={'center'} gap={1}>
              <TokenIcon />
              <Typography variant='r'>
                {asset}
              </Typography>
            </Grid>
            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              <Typography variant='s'>
                Deposited
              </Typography>
              <Grid item>
                <Typography variant='s' component={'div'}>{`${displayNumber(assetAmount, 6, 2)}`}</Typography>
                <Typography variant='xs' color='text.secondary'>${displayNumber(assetValue, 2, 2)}</Typography>
              </Grid>
            </Grid>

            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              <Typography variant='s'>
                APY
              </Typography>
              <Typography variant='s' component={'div'}>{assetApy?.toFixed(2)}%</Typography>
            </Grid>

            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              {
                isDeposited && (
                  <Grid container spacing={0} flexDirection={'row'} gap={2} flexWrap={'nowrap'}>
                    <Grid item xs={6}>
                      <Button onClick={() => navigateTo(`/token/${data.asset}`)}
                        variant='contained' color="secondary" sx={{ width: '100%' }}>
                        Deposit
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button onClick={() => navigateTo(`/token/${data.asset}`)}
                        variant='contained' color="secondary" sx={{ width: '100%' }}>
                        Withdraw
                      </Button>
                    </Grid>
                  </Grid>
                )
              }
              {
                isBorrowed && (
                  <Grid container gap={1}>
                    <Button variant='contained' color="secondary">
                      Borrow
                    </Button>
                    <Button variant='contained' color="secondary">
                      Repay
                    </Button>
                  </Grid>
                )
              }
            </Grid>
          </Grid>
        </StyledTableCell>
      </TableRow>
    )
  })
}